import React, { useContext, useState } from 'react'

// components
import { IUser } from 'interfaces/user'
import { useProfile } from 'containers/profile/hooks/useProfile'

// styles
import dynamic from 'next/dynamic'
import { PersonalInfo } from './profile-bar/PersonalInfo'
import { SocialNetworkList } from './profile-bar/SocialNetworkList'
import { LocationAndWebsite } from './profile-bar/LocationAndWebsite'
import { ActionButtons } from '../../components/buttons/ActionButtons'
import { SkeletonAvatar } from '@/components/skeleton/AvatarSkeleton'
import { cssClass } from '@/shared/utils'
import { NavigationTabs } from './profile-bar/NavigationTabs'
import { skeleton } from '@/components/skeleton/styles/index.css'
import { ProfileStats } from './profile-bar/ProfileStats'
import {
  profileBarLeft,
  profileBarRight,
} from 'containers/profile/styles/baseLayout.css'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { useUser } from 'containers/user/hooks/useUser'
import { BrowserContext } from 'context'
import { MutualConnections } from 'containers/profile-network/components/mutual-connections/MutualConnections'

const AvatarComponent = dynamic(
  () =>
    import('containers/recommendations/Avatar').then((module) => module.Avatar),
  { ssr: false, loading: () => <SkeletonAvatar size={90} /> }
)

export const ProfileBar: React.FC<IUser> = () => {
  const { profile, isLoading } = useProfile()
  const { isAuth } = useUser()
  const { isMobile } = useContext(BrowserContext)
  const [show, setShow] = useState(false)

  const handleAvatarModal = (state: boolean) => {
    const checklist = document.querySelector('#profile-onboarding-checklist')

    if (checklist) {
      const classList = checklist.classList

      state ? classList.add('hidden') : classList.remove('hidden')
    }

    setShow(state)
  }

  return (
    <>
      <div className="bg-white-primary rounded-t-12 rounded-t-0--tablet -mt-20 relative ">
        <div className="flex gap-20 gap-24--tablet pb-24 pt-16 px-20">
          <div className="hidden--tablet">
            <AvatarComponent
              image={profile?.avatar}
              size={90}
              username={profile?.username}
              isLoading={isLoading}
              isHiring={profile?.isHiring}
              onClick={() => isAuth && handleAvatarModal(true)}
              className={cssClass([isAuth && 'cursor-pointer'])}
              alt={profile?.username + '_avatar_profile_bar'}
            />
          </div>
          <div className="hidden block--tablet -mt-40">
            <AvatarComponent
              image={profile?.avatar}
              size={120}
              username={profile?.username}
              isLoading={isLoading}
              isHiring={profile?.isHiring}
              onClick={() => isAuth && handleAvatarModal(true)}
              className={cssClass([isAuth && 'cursor-pointer'])}
              alt={profile?.username + '_avatar_profile_bar_desktop'}
            />
          </div>
          <div
            className={cssClass([
              'flex--desktopXl justify-space-between full-w break-word',
              profileBarLeft,
            ])}
          >
            <div className="flex flex-column gap-10 full-w">
              <PersonalInfo />
              <div className="hidden--desktopXl">
                <SocialNetworkList />
              </div>
              <LocationAndWebsite />
              <div className="hidden--desktopXl">
                <ActionButtons />
              </div>
              <div className="hidden block--desktopXl">
                <ProfileStats version="mobile" />
              </div>
              <div className="hidden block--desktopXl">
                <MutualConnections />
              </div>
            </div>
            <div
              className={cssClass([
                'hidden flex--desktopXl flex-column items-flex-end justify-fles-end gap-36',
                profileBarRight,
              ])}
            >
              <SocialNetworkList />
              <ActionButtons />
            </div>
          </div>
        </div>

        <MutualConnections className="hidden--desktopXl px-20 pb-20" />

        <div className="hidden--desktopXl">
          <ProfileStats version="desktop" />
        </div>
        {isLoading ? (
          <div className="full-w px-12 pb-4 pt-16">
            <div
              style={{
                height: 52,
              }}
              className={cssClass([skeleton, 'full-w'])}
            />
          </div>
        ) : (
          <NavigationTabs />
        )}
      </div>

      {isAuth && (
        <ModalPage
          show={show}
          onClose={() => handleAvatarModal(false)}
          className={['full-w', isMobile && 'p-40']}
        >
          <div className="flex flex-column gap-30">
            <div className={'flex full-w items-center justify-center'}>
              <AvatarComponent
                size={isMobile ? 270 : 360}
                image={profile?.avatar}
                username={profile?.username}
                isLoading={isLoading}
              />
            </div>
          </div>
        </ModalPage>
      )}
    </>
  )
}
